import type { Language } from '@eformulieren/engine'
import { LanguageSwitch, type InvitationState, type LoginOption, InvitationChoice } from '@eformulieren/renderer'
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { ProductBar } from './ProductBar.js'
import { LogoutButton } from './LogoutButton.js'
import { getWhoami } from './contextState.js'
import { targetFromPage, getEntries, loginOptions, withoutSession } from './login.js'
import { type Outcome, outcomeFromResponse, clearCode, targetAfterAccept, viewOutcome } from './invitation.js'
import { WRITE_HEADERS } from './podRequests.js'
import { NO_CONNECTION, noticeForRejection } from './rejection.js'

/**
 * De pagina achter de link uit een uitnodiging (R-64c, R-155e, besluiten 4 en 5 van 30 oktober 2026,
 * ontwerp 4.8 en 9.2, mockups 04 en 05 van delen).
 *
 * `main.tsx` heeft de code al uit het fragment gehaald, uit de adresbalk gewist en in dit tabblad
 * bewaard (`invitation.ts`). Deze pagina stelt vast wie er kijkt (`GET /whoami`), en vraagt de pod dan
 * de uitnodiging te tonen zonder haar te verbruiken (`POST /uitnodiging/inzien`): van wie, welk
 * formulier, welke rechten, tot wanneer, en met welk account de ontvanger is ingelogd. Daarna aannemen
 * of weigeren. Aannemen gaat naar het formulier van de instantie uit het antwoord, en bij een
 * adviesvraag naar de adviseurpagina (`adviserRoute.ts`).
 *
 * **Niet bruikbaar** zegt de pagina in één melding met wat er aan de hand kan zijn en wat nu; de
 * reden alleen als de pod haar noemt, en dat doet hij alleen bij het adres van de uitnodiging
 * (besluit 4). Dan gaat ook de bewaarde code weg.
 *
 * **Zonder sessie** een knop per ingang naar de inlogdienst ("Inloggen", "Ik werk bij NWO"). Die
 * stuurt na het inloggen terug naar `/uitnodiging`; de code staat dan nog in dit tabblad. Kon de pod
 * niet vaststellen of er een sessie is, dan zegt de pagina dat het nu niet lukt, met opnieuw proberen.
 *
 * **"Niet u?"** biedt sinds 31 oktober 2026 uitloggen (`LogoutButton`, de inlogdienst wist dan de
 * sessie en de podcookies) en de ingangen om met een ander account in te loggen. Die ingangen dragen
 * `account=kiezen`: de inlogdienst slaat zijn eigen sessie dan over en laat de identiteitsprovider het
 * account kiezen, want anders stuurt een lopende sessie meteen hetzelfde account terug. Wie een
 * uitnodiging die hij zelf aannam opnieuw opent, gaat meteen naar zijn formulier (R-64c).
 */

export interface InvitationPageProps {
  readonly taal: Language
  readonly onLanguage: (language: Language) => void
  /** De code uit het fragment of uit dit tabblad, of niets als er geen is. */
  readonly code: string | undefined
  /** Waar de code tot na het inloggen staat; gewist zodra er een antwoord is. */
  readonly opslag: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
}

export function InvitationPage({ taal: language, onLanguage, code, opslag: storage }: InvitationPageProps): ReactNode {
  const [state, setState] = useState<InvitationState>(code === undefined ? { soort: 'ongeldig' } : { soort: 'wachten' })
  /** De code van de pod bij een poging die niet doorging; de tekst pas bij het tonen (R-144). */
  const [blockedCode, setFailedCode] = useState<string | undefined>(undefined)
  /** Waar aannemen heen gaat; bekend zodra de pod de uitnodiging toonde. */
  const doel = useRef<'deling' | 'advies'>('deling')
  /** Het antwoord van de pod, zodat een andere taal de titels in die taal toont zonder nieuw verzoek. */
  const [viewed, setViewed] = useState<{ readonly status: number; readonly body: string } | undefined>(undefined)

  /** Wie er kijkt, zodra `/whoami` dat zei; voor "Uitloggen". */
  const [persoon, setPersoon] = useState<string | undefined>(undefined)

  /**
   * De ingangen om met een ander account in te loggen, met dit adres als doel; leeg bij tegenslag. Met
   * `account=kiezen`, zodat een lopende sessie niet meteen hetzelfde account terugstuurt.
   */
  const options = useCallback(async (): Promise<readonly LoginOption[]> => {
    const entries = await getEntries()
    return entries === undefined ? [] : loginOptions(entries, targetFromPage(window.location), { anderAccount: true })
  }, [])

  /** Geen sessie (meer): de keuze hoe de ontvanger inlogt, of een storing als de pod niet antwoordt. */
  const toLogin = useCallback(async (): Promise<void> => {
    setState(withoutSession(await getEntries(), targetFromPage(window.location)))
  }, [])

  /** De uitnodiging bekijken zonder haar te verbruiken (`api-contract.md` 2). */
  const view = useCallback(async (): Promise<void> => {
    if (code === undefined) return
    const path = '/uitnodiging/inzien'
    let status = 0
    let body = ''
    try {
      // De code in het lichaam en nooit in het pad of de query: die staan in elk log.
      const response = await fetch(path, { method: 'POST', headers: WRITE_HEADERS, body: JSON.stringify({ code }) })
      status = response.status
      body = await response.text()
      if (!response.ok) {
        console.error('eformulieren: uitnodiging niet te bekijken', { methode: 'POST', pad: path, status, lijf: body.slice(0, 2000) })
      }
    } catch (error) {
      console.error('eformulieren: uitnodiging niet te bekijken', { methode: 'POST', pad: path, fout: error })
    }
    const outcome = viewOutcome(status, body, language)
    switch (outcome.soort) {
      case 'bekijken':
        doel.current = outcome.doel
        setViewed({ status, body })
        setState({ soort: 'keuze', uitnodiging: outcome.uitnodiging, opties: await options() })
        return
      case 'ongeldig':
        clearCode(storage)
        setState({ soort: 'ongeldig', opties: await options() })
        return
      case 'al-aangenomen':
        // Wie terugkomt via de link van een uitnodiging die hij al aannam, gaat naar zijn formulier
        // (R-64c). `replace`: terug in de geschiedenis hoort niet weer deze pagina te zijn.
        clearCode(storage)
        window.location.replace(targetAfterAccept(outcome.instantieId, outcome.doel))
        return
      case 'onbruikbaar':
        clearCode(storage)
        setState({
          soort: 'onbruikbaar',
          reden: outcome.reden,
          ...(outcome.geldigTot === undefined ? {} : { geldigTot: outcome.geldigTot }),
          // Al aangenomen, en niet door wie nu kijkt: "Niet u?" met een ander account (R-64c).
          ...(outcome.reden === 'al-aangenomen' ? { opties: await options() } : {}),
        })
        return
      case 'andere-ingang':
        setState({ soort: 'andere-ingang', kant: outcome.kant, opties: await options() })
        return
      case 'inloggen':
        await toLogin()
        return
      case 'zonder-gegevens':
        setState({ soort: 'keuze' })
    }
  }, [code, language, options, storage, toLogin])

  const whoIsLooking = useCallback(async (): Promise<void> => {
    if (code === undefined) return
    setState({ soort: 'wachten' })
    const outcome = await getWhoami()
    if (outcome.soort === 'geen-sessie') {
      await toLogin()
      return
    }
    if (outcome.soort !== 'persoon') {
      setState({ soort: 'storing' })
      return
    }
    setPersoon(outcome.persoon)
    await view()
  }, [code, toLogin, view])

  useEffect(() => {
    void whoIsLooking()
    // Alleen bij binnenkomst en bij een andere code; een andere taal haalt de uitnodiging niet opnieuw.
  }, [code])

  function process(outcome: Outcome): void {
    switch (outcome.soort) {
      case 'naar-formulier':
        clearCode(storage)
        // `replace` en niet `assign`: terug in de geschiedenis hoort niet weer deze pagina te zijn.
        window.location.replace(targetAfterAccept(outcome.instantieId, doel.current))
        return
      case 'geweigerd':
        clearCode(storage)
        setState({ soort: 'geweigerd' })
        return
      case 'ongeldig':
        clearCode(storage)
        setState({ soort: 'ongeldig' })
        return
      case 'inloggen':
        void toLogin()
        return
      case 'opnieuw':
        setFailedCode(outcome.code ?? NO_CONNECTION)
        setState((previous) => (previous.soort === 'keuze' ? { ...previous, bezig: false } : { soort: 'keuze' }))
    }
  }

  async function respond(operation: 'aannemen' | 'weigeren'): Promise<void> {
    if (code === undefined) return
    const path = operation === 'aannemen' ? '/uitnodiging' : '/uitnodiging/weigeren'
    setFailedCode(undefined)
    setState((previous) => (previous.soort === 'keuze' ? { ...previous, bezig: true } : { soort: 'keuze', bezig: true }))
    try {
      const response = await fetch(path, { method: 'POST', headers: WRITE_HEADERS, body: JSON.stringify({ code }) })
      const body = await response.text()
      if (!response.ok) {
        // De keten naar de console, zonder de code en zonder het lijf van het verzoek.
        console.error('eformulieren: uitnodiging niet beantwoord', { methode: 'POST', pad: path, status: response.status, lijf: body.slice(0, 2000) })
      }
      process(outcomeFromResponse(operation, response.status, body))
    } catch (error) {
      console.error('eformulieren: uitnodiging niet beantwoord', { methode: 'POST', pad: path, fout: error })
      process({ soort: 'opnieuw' })
    }
  }

  const again = viewed === undefined ? undefined : viewOutcome(viewed.status, viewed.body, language)
  const inLanguage: InvitationState =
    state.soort === 'keuze' && state.uitnodiging !== undefined && again?.soort === 'bekijken' ? { ...state, uitnodiging: again.uitnodiging } : state
  const shown: InvitationState =
    inLanguage.soort === 'keuze' && blockedCode !== undefined ? { ...inLanguage, melding: noticeForRejection(blockedCode, language) } : inLanguage

  return (
    <>
      <ProductBar taal={language} actions={<LanguageSwitch taal={language} onLanguage={onLanguage} compact />} />
      <div className="pod-uitnodiging" data-proef="uitnodiging" data-stand={state.soort}>
        <InvitationChoice
          taal={language}
          stand={shown}
          onAccept={() => void respond('aannemen')}
          onReject={() => void respond('weigeren')}
          onAgain={() => void whoIsLooking()}
          {...(persoon === undefined ? {} : { uitloggen: <LogoutButton taal={language} persoon={persoon} /> })}
        />
      </div>
    </>
  )
}
